import { KeyboardEvent, PropsWithChildren, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  useFormState,
} from 'react-hook-form';
import FormField from '@ingka/form-field';
import InputField, { InputFieldProps } from '@ingka/input-field';

import { getter } from '@/lib/utils/object';
import { extractErrorMessage } from '../error-message';

import styles from './index.module.css';

export type ControlledInputProps<T extends FieldValues> = {
  className?: string;
  placeholderName?: FieldPath<T>;
  // Form
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: any;
  // Ingka form field props
  type: InputFieldProps['type'];
  label: React.ReactNode;
  readOnly?: boolean;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: () => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  suffixLabel?: string;
};

export function ControlledInput<T extends FieldValues>(
  props: PropsWithChildren<ControlledInputProps<T>>
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    name,
    rules,
    shouldValidate = true,
    label,
    readOnly,
    successMessage,
    type,

    onChange,
    onKeyDown,
    forcedError,
    suffixLabel,
  } = props;

  // Error handling
  const { errors } = useFormState({ name });
  const error = forcedError || getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);

  return (
    <div className={props.className}>
      <Controller
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormField
            fieldHelper={{
              msg: shouldValidate ? successMessage : '',
              type: 'error',
            }}
            validation={{
              msg: errorMsg,
              type: 'error',
            }}
            valid={!error}
            shouldValidate={!!shouldValidate}
          >
            <InputField
              {...field}
              ref={inputRef}
              onChange={(e) => {
                // Use native "react-hook-form" controlled handler
                field.onChange(e);
                // This is to allow custom change handlers, like the one that "ControlledMultilanguageInput" uses
                onChange?.();
              }}
              onKeyDownCapture={onKeyDown}
              type={type}
              className={styles.input}
              iconPosition="leading"
              id={name}
              data-testid={`${name}Input`}
              label={label}
              readOnly={readOnly}
              value={field.value || ''}
              suffixLabel={suffixLabel}
            />
          </FormField>
        )}
      />
    </div>
  );
}

export default ControlledInput;
